/* eslint-disable jsx-a11y/aria-role */
import { Image as SitecoreImage } from '@sitecore-jss/sitecore-jss-react';
import { classnames } from 'tailwindcss-classnames';
import { useCallback, useEffect, useState } from 'react';
import Search from 'components/authorable/site/Header/SvgSearch/search';
import Menu from 'components/authorable/site/Header/SvgMenu/menu';
import MenuClose from 'components/authorable/site/Header/SvgMenuClose/menu-close';
import ArrowDown from 'components/authorable/site/Header/SvgArrowDown/arrow-down';
import SearchIcon from 'components/helpers/SvgIcon/icons/icon-Search';
import Container from 'components/helpers/Container';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import { TextWithOptionalLineBreaks } from 'components/helpers/Heading';
import {
  logoNavigationGtmPush,
  menuLocation,
  pageNavigationDataLayerPush,
} from 'lib/util/gtm-datalayer';
import {
  LinkForNavPageOrLink,
  RitnHeaderNavRoot,
  isNavPageOrLink,
  isNavigationHeader,
} from 'lib/util/site-nav';
import { logWarning } from 'lib/util/errors';

export type RitnHeaderProps = {
  ritnroot?: RitnHeaderNavRoot;
};
/**
 * Logo Navigation Push with MenuLocation 0
 */
const logoNavGtmPush = () => logoNavigationGtmPush(menuLocation[0]);

const RitnHeader = ({ ritnroot }: RitnHeaderProps): JSX.Element => {
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const [subMenuIsOpen, setsubMenuIsOpen] = useState({
    status: false,
    from: '',
  });
  useEffect(() => {
    setIsOpen(false);
    setsubMenuIsOpen({
      status: false,
      from: '',
    });
  }, [router?.asPath]);

  const root = ritnroot;
  const placeholderText = ritnroot?.fields?.searchPlaceholderText.value
    ? ritnroot?.fields?.searchPlaceholderText.value
    : '';
  const menuBar = classnames(
    'xl:flex',
    'justify-start',
    'leading-3',
    'text-blue',
    'text-base',
    'border-0',
    'border-gray-quartz',
    'bg-blue-navy'
  );
  const dropMenuItem = classnames('text-white', 'xl:text-black', 'p-1', 'pl-6', 'xl:pl-1');
  const hasSubmenu = classnames(
    'has-submenu',
    'border-b',
    'border-gray-quartz',
    'xl:border-t-0',
    'xl:border-b-0',
    'menu-item-border-width',
    'relative'
  );
  const noSubmenu = classnames(
    'text-white',
    'px-4',
    'relative',
    'border-b',
    'border-gray-quartz',
    'xl:border-t-0',
    'xl:border-b-0',
    'py-4',
    'xl:py-3',
    'menu-item-border-width',
    'xl:px-8'
  );
  const subMenuBtn = classnames(
    'leading-3',
    'text-base',
    'w-full',
    'text-left',
    'xl:border-0',
    'px-4',
    'hover:cursor-pointer',
    'relative',
    'text-white',
    'hover:text-white',
    'py-4',
    'xl:py-3',
    'xl:px-8'
  );
  const subMenu = classnames(
    'z-20',
    'bg-blue-navy',
    'xl:bg-white',
    'hidden',
    'relative',
    'left-0',
    'right-0',
    'xl:absolute',
    'xl:w-64',
    'dropdown-menu-border'
  );

  const toggleMobileMenu = () => {
    setIsOpen((prev) => !prev);
    const displayText = isOpen ? 'Close' : 'Menu';
    pageNavigationDataLayerPush(displayText, menuLocation[1]);
  };

  const toggleSubMenu = (navId: string | '', displayText: string) => {
    setsubMenuIsOpen({ status: !subMenuIsOpen?.status, from: navId });
    pageNavigationDataLayerPush(displayText, menuLocation[1]);
  };

  const utilityLinks = root?.fields?.utilityLinks;
  const getHeaderData = root?.children?.map((item) => {
    if (isNavigationHeader(item)) {
      return (
        <li
          role="menuitem"
          key={item.id}
          className={
            subMenuIsOpen?.status && subMenuIsOpen?.from === `nav-item--${item.id}`
              ? classnames(hasSubmenu, 'state-open')
              : classnames(hasSubmenu)
          }
        >
          <button
            aria-label="Toggle sub navigation menu"
            type="button"
            role="tab"
            aria-selected="true"
            aria-controls="tabpanel-id"
            onClick={() => toggleSubMenu(`nav-item--${item.id}`, item.name)}
            className={classnames(subMenuBtn)}
          >
            {item.fields.navigationHeading.value}
            <ArrowDown
              aria-hidden="true"
              className={classnames(
                'svg-arrow-down',
                'inline-block',
                'ml-2',
                'float-right',
                'xl:float-none'
              )}
            />
          </button>
          <ul role="menu" className={classnames(subMenu)}>
            {item.children?.map((item2) => {
              const navDropItem = item2;
              return (
                <li
                  role="menuitem"
                  key={navDropItem.id}
                  className={classnames(dropMenuItem)}
                  aria-label="Menu item"
                >
                  <LinkForNavPageOrLink item={navDropItem} />
                </li>
              );
            })}
          </ul>
        </li>
      );
    }
    if (isNavPageOrLink(item)) {
      return (
        <li key={item.id} className={classnames(noSubmenu)}>
          <LinkForNavPageOrLink item={item} onClick={toggleMobileMenu} />
        </li>
      );
    }
    logWarning('ritn nav - could not identify nav item type', { item });
    return <></>;
  });

  const handleDocumentClick = useCallback(
    (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (target.closest('a') && subMenuIsOpen) {
        setsubMenuIsOpen({
          status: false,
          from: '',
        });
      }
    },
    [subMenuIsOpen, setsubMenuIsOpen]
  );
  useEffect(() => {
    document.addEventListener('click', handleDocumentClick);
    return () => {
      document.removeEventListener('click', handleDocumentClick);
    };
  }, [handleDocumentClick]);

  return (
    <>
      <div
        className={classnames('xl:hidden', { flex: !isOpen }, 'xl:block', {
          'border-mobile-menu': !isOpen,
        })}
      >
        <button
          type="button"
          aria-label="Toggle navigation menu"
          className={classnames(
            'bg-white',
            'relative',
            'flex',
            'flex-row',
            'mx-2',
            'my-auto',
            'z-20',
            { 'pt-2': !isOpen, 'py-4': isOpen, 'pr-2': isOpen },
            'w-1/6',
            { 'float-left': !isOpen, 'float-right': isOpen },
            'h-full'
          )}
          data-id="header-mobile-menu-toggle"
          onClick={toggleMobileMenu}
        >
          <div
            className={classnames(
              'flex',
              'justify-start',
              'w-full',
              'text-right',
              'align-middle',
              'pl-3',
              'text-body',
              'text-blue',
              'hover:text-white',
              'font-semibold'
            )}
          >
            <div
              className={classnames(
                'flex',
                'text-right',
                'text-body',
                'text-blue',
                'hover:text-blue-navy',
                'font-semibold',
                { 'flex-col': !isOpen },
                { 'float-left': !isOpen, 'float-right': isOpen }
              )}
            >
              {isOpen ? <span className={classnames('px-2.5', 'leading-2')}>Close</span> : ''}
              {isOpen ? <MenuClose /> : <Menu className={classnames('svg-menu')} />}
              {isOpen ? (
                ''
              ) : (
                <span className={classnames('leading-7', 'text-blue-navy', 'text-xxxs')}>Menu</span>
              )}
            </div>
          </div>
        </button>
        <div
          className={classnames('align-middle', 'xl:block', 'w-4/6', {
            block: !isOpen,
            hidden: isOpen,
          })}
        >
          <NextLink href="/">
            <a href="/" aria-label="Logo link" onClick={logoNavGtmPush}>
              <SitecoreImage
                field={root?.fields?.logo}
                className={classnames('h-full', 'mx-auto')}
              />
            </a>
          </NextLink>
        </div>
        <button
          type="button"
          aria-label="Toggle navigation menu"
          className={classnames(
            'bg-white',
            'relative',
            'flex',
            'flex-row',
            'mx-2',
            'my-auto',
            'z-20',
            'w-1/6',
            { 'float-left': !isOpen, 'float-right': isOpen },
            { block: !isOpen, hidden: isOpen },
            { 'h-full': !isOpen, 'h-7': isOpen }
          )}
          data-id="header-mobile-menu-toggle"
          onClick={toggleMobileMenu}
        >
          <div
            className={classnames(
              'flex',
              'justify-around',
              'w-full',
              'text-right',
              'align-middle',
              'pr-3',
              'text-body',
              'text-blue',
              'hover:text-white',
              'font-semibold',
              'flex-col'
            )}
          >
            {isOpen ? (
              ''
            ) : (
              <div>
                <div
                  className={classnames(
                    'aa-IconWrapper',
                    'ritnHeader',
                    'h-8',
                    'w-8',
                    'overflow-hidden',
                    'float-right'
                  )}
                >
                  <SearchIcon className={classnames('svg-search', 'h-8', 'w-8')} />
                </div>
              </div>
            )}
            {isOpen ? (
              ''
            ) : (
              <span className={classnames('leading-1', 'text-blue-navy', 'text-xxxs')}>Search</span>
            )}
          </div>
        </button>
      </div>
      <div className={classnames('xl:block', 'relative', { block: isOpen, hidden: !isOpen })}>
        <nav
          aria-label="Be the match utility navigation"
          className={classnames(
            'max-w-full',
            'w-full',
            'xl:mx-auto',
            'flex',
            'flex-col',
            'xl:flex-row',
            'xl:h-28',
            'overflow-hidden',
            'xl:items-center'
          )}
        >
          <Container
            size="standard"
            className={classnames(
              'w-full',
              'xl:mx-auto',
              'flex',
              'flex-col',
              'xl:flex-row',
              'xl:h-28',
              'overflow-hidden',
              'xl:items-center'
            )}
          >
            <NextLink href="/">
              <a
                className={classnames('px-4')}
                href="/"
                aria-label="Logo link"
                onClick={logoNavGtmPush}
              >
                <SitecoreImage
                  field={root?.fields?.logo}
                  className={classnames('hidden', 'xl:block', 'min-w-[250px]', 'py-4', 'h-28')}
                />
              </a>
            </NextLink>
            <div
              className={classnames(
                'w-3/12',
                'pl-5',
                'border-left-blue-navy',
                'font-semibold',
                'xl:block',
                'hidden'
              )}
            >
              <TextWithOptionalLineBreaks tag="" field={root?.fields?.headerTitle} editable />
            </div>

            <ul
              role="menu"
              className={classnames(
                'hidden',
                'px-4',
                'text-blue',
                'xl:flex',
                'xl:top-auto',
                'xl:relative',
                'xl:flex-1',
                'xl:flex-row',
                'justify-end'
              )}
            >
              {utilityLinks?.map((item) =>
                isNavPageOrLink(item) ? (
                  <li
                    role="menuitem"
                    key={`utility_${item.id ?? item.name}`}
                    className={classnames('py-3')}
                    aria-label="Menu item"
                  >
                    <LinkForNavPageOrLink
                      item={item}
                      className={classnames(
                        'block',
                        'font-semibold',
                        'text-theme-btn-link-text',
                        'leading-3',
                        'xl:text-sm',
                        'xl:leading-3',
                        'xl:px-4',
                        'hover:underline',
                        'text-blue'
                      )}
                    />
                  </li>
                ) : (
                  <></>
                )
              )}
            </ul>
            <div
              className={classnames(
                'relative',
                { 'bg-gray-solitude': isOpen },
                'p-0',
                'xl:bg-none',
                'xl:pr-2.5',
                'ritn-Desktop'
              )}
            >
              <Search searchplaceholderText={placeholderText} />
            </div>
          </Container>
        </nav>
        <nav
          aria-label="Be the match main navigation"
          className={classnames('max-w-full', 'w-full', 'xl:mx-auto', 'bg-blue-navy')}
        >
          <Container
            size="standard"
            className={classnames(
              'w-full',
              'xl:mx-auto',
              'flex',
              'flex-col',
              'xl:flex-row',
              'xl:items-center',
              'xl:mb-px'
            )}
          >
            <ul
              className={classnames(menuBar)}
              role="ritnmenubar"
              aria-label="Be the match main menu"
            >
              {getHeaderData}
            </ul>
          </Container>
        </nav>
        <nav
          aria-label="Be the match utility mobile navigation"
          className={classnames(
            'max-w-screen-2xl',
            'w-full',
            'bg-blue-navy',
            'flex',
            'flex-col',
            'overflow-hidden',
            'xl:hidden'
          )}
        >
          <ul
            role="menu"
            className={classnames(
              'flex',
              'flex-col',
              'w-full',
              'px-4',
              'bg-blue-navy',
              'text-white',
              'justify-end'
            )}
          >
            {utilityLinks?.map((item) =>
              isNavPageOrLink(item) ? (
                <li
                  role="menuitem"
                  key={item.id ?? item.name}
                  className={classnames('py-3')}
                  aria-label="Menu item"
                >
                  <LinkForNavPageOrLink
                    item={item}
                    className={classnames(
                      'block',
                      'font-semibold',
                      'text-theme-btn-link-text',
                      'leading-3',
                      'hover:underline',
                      'text-white',
                      'xl:pr-5'
                    )}
                  />
                </li>
              ) : (
                <></>
              )
            )}
          </ul>
        </nav>
      </div>
    </>
  );
};
export default RitnHeader;
